// Open-Meteo API utility functions
// Documentation: https://open-meteo.com/en/docs

export function buildForecastUrl(lat: number, lon: number) {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    current: 'temperature_2m,relative_humidity_2m,apparent_temperature,precipitation,rain,weather_code,cloud_cover,pressure_msl,wind_speed_10m,wind_direction_10m,wind_gusts_10m',
    hourly: 'temperature_2m,relative_humidity_2m,dew_point_2m,precipitation,rain,wind_speed_10m,wind_gusts_10m,wind_direction_10m,cloud_cover,pressure_msl,visibility,uv_index',
    daily: 'temperature_2m_max,temperature_2m_min,uv_index_max,precipitation_sum,precipitation_hours,wind_speed_10m_max,wind_gusts_10m_max,weather_code,sunrise,sunset',
    forecast_days: '7',
    timeformat: 'iso8601',
    timezone: 'auto',
    temperature_unit: 'celsius',
    wind_speed_unit: 'kmh',
    precipitation_unit: 'mm',
  });
  return `https://api.open-meteo.com/v1/forecast?${params.toString()}`;
}

export function pickHourlyNext24h(resp: any) {
  const { time } = resp.hourly;
  const nowIso = new Date().toISOString().slice(0, 13) + ':00';
  const startIdx = Math.max(0, time.findIndex((t: string) => t >= nowIso));
  const endIdx = Math.min(time.length, startIdx + 24);
  const keys = ['temperature_2m','relative_humidity_2m','precipitation','wind_speed_10m','wind_gusts_10m','weather_code'];
  return Array.from({ length: endIdx - startIdx }, (_, i) => {
    const idx = startIdx + i;
    const row: any = { time: resp.hourly.time[idx] };
    keys.forEach(k => { row[k] = resp.hourly[k]?.[idx] ?? null; });
    return row;
  });
}

export function mapDaily7d(resp: any) {
  const len = resp.daily?.time?.length ?? 0;
  const out = [];
  for (let i = 0; i < len; i++) {
    out.push({
      date: resp.daily.time[i],
      temperature_2m_max: resp.daily.temperature_2m_max?.[i],
      temperature_2m_min: resp.daily.temperature_2m_min?.[i],
      uv_index_max: resp.daily.uv_index_max?.[i],
      precipitation_sum: resp.daily.precipitation_sum?.[i],
      wind_gusts_10m_max: resp.daily.wind_gusts_10m_max?.[i],
      weather_code: resp.daily.weather_code?.[i],
      sunrise: resp.daily.sunrise?.[i],
      sunset: resp.daily.sunset?.[i],
    });
  }
  return out;
}

// Convert Open-Meteo weather code to our simplified weather condition
export function weatherCodeToCondition(code: number): string {
  // Based on WMO Weather interpretation codes
  // https://open-meteo.com/en/docs#weathervariables
  if (code === 0) return 'Sunny'; // Clear sky
  if (code >= 1 && code <= 3) return 'Partly Cloudy'; // Mainly clear, partly cloudy, overcast
  if (code >= 45 && code <= 48) return 'Cloudy'; // Fog variants
  if (code >= 51 && code <= 67) return 'Rainy'; // Drizzle and rain variants
  if (code >= 71 && code <= 86) return 'Rainy'; // Snow and rain showers (treating as rainy)
  if (code >= 95 && code <= 99) return 'Rainy'; // Thunderstorm
  return 'Partly Cloudy'; // Default fallback
}

// Get weather description from code
export function getWeatherDescription(code: number): string {
  const descriptions: { [key: number]: string } = {
    0: 'Clear sky',
    1: 'Mainly clear',
    2: 'Partly cloudy',
    3: 'Overcast',
    45: 'Fog',
    48: 'Depositing rime fog',
    51: 'Light drizzle',
    53: 'Moderate drizzle',
    55: 'Dense drizzle',
    56: 'Light freezing drizzle',
    57: 'Dense freezing drizzle',
    61: 'Slight rain',
    63: 'Moderate rain',
    65: 'Heavy rain',
    66: 'Light freezing rain',
    67: 'Heavy freezing rain',
    71: 'Slight snow fall',
    73: 'Moderate snow fall',
    75: 'Heavy snow fall',
    77: 'Snow grains',
    80: 'Slight rain showers',
    81: 'Moderate rain showers',
    82: 'Violent rain showers',
    85: 'Slight snow showers',
    86: 'Heavy snow showers',
    95: 'Thunderstorm',
    96: 'Thunderstorm with slight hail',
    99: 'Thunderstorm with heavy hail'
  };
  return descriptions[code] || 'Unknown conditions';
}
